import reportService from "@/services/mrfiktiv/services/reportService";
import {
  MrfiktivPageFilterElementGen,
  MrfiktivReportControllerFindAllParamsGen,
  MrfiktivReportViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import Vue from "vue";
import { Action, Module, getModule } from "vuex-module-decorators";
import { ProgressStatusEnum } from "../enum/partner/progress.status.enum";
import { FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import { FleetAggregationModule } from "./fleet-aggregation.store";
import { PartnerUserModule } from "./partner-user.store";
import { ThgPageFilterElementGen } from "@/services/thg/v1/data-contracts";
import {
  BackwardsCompatibleDataAccessLayer,
  BackwardsCompatiblePaginatedStore
} from "../backwards-compatible-paginated.store";
import { PageDataHandler } from "@/lib/utility/data/page-data-handler";

export const ReportDataAccessLayer = new BackwardsCompatibleDataAccessLayer<MrfiktivReportViewModelGen>();

@Module({
  dynamic: true,
  namespaced: true,
  name: "reportPagination",
  store
})
export class ReportPaginationStore extends BackwardsCompatiblePaginatedStore<
  MrfiktivReportViewModelGen,
  MrfiktivReportControllerFindAllParamsGen
> {
  _data = ReportDataAccessLayer;
  _pager = new PageDataHandler(this._data, this._pageProvider);

  protected _isLoadAll = false;
  filterOptions: IPaginationFilterListElement[] = [
    { key: "timestamps.created", type: FilterTypes.DATE, displayName: "objects.timestamp.created" },
    { key: "numberplate", type: FilterTypes.STRING, displayName: "objects.report.numberplate" },
    { key: "customerName", type: FilterTypes.STRING, displayName: "objects.report.customerName" },
    {
      key: "assignees",
      type: FilterTypes.OBJECT_ID,
      displayName: "objects.report.assignees",
      config: {
        items: PartnerUserModule.paginationList,
        mapItemToComponent: (item: any) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-user"
      }
    },
    { key: "customerContact.email", type: FilterTypes.STRING, displayName: "objects.report.customerContact.email" },
    { key: "customerContact.phone", type: FilterTypes.STRING, displayName: "objects.report.customerContact.phone" },
    { key: "marketingOptIn", type: FilterTypes.BOOLEAN, displayName: "objects.report.marketingOptIn" },
    {
      key: "progressStatus",
      type: FilterTypes.ENUM,
      config: { items: Object.values(ProgressStatusEnum) },
      displayName: "objects.report.progressStatus"
    },

    {
      key: "registrationResults.firstregistrationDay",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationDay"
    },
    {
      key: "registrationResults.firstregistrationMonth",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationMonth"
    },
    {
      key: "registrationResults.firstregistrationYear",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.firstregistrationYear"
    },

    {
      key: "registrationResults.street",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.street"
    },
    {
      key: "registrationResults.zipCode",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.zipCode"
    },
    {
      key: "registrationResults.city",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.city"
    },
    {
      key: "registrationResults.huYear",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.huYear"
    },
    {
      key: "registrationResults.huMonth",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.huMonth"
    },
    {
      key: "registrationResults.manufacturerNameCode",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerNameCode"
    },
    {
      key: "registrationResults.manufacturerTypeCode",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerTypeCode"
    },
    {
      key: "registrationResults.driveTyp",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.driveTyp"
    },
    {
      key: "registrationResults.identificationnumber",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.identificationnumber"
    },
    {
      key: "registrationResults.numberplate",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.numberplate"
    },
    {
      key: "registrationResults.manufacturerName",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerName"
    },
    {
      key: "registrationResults.manufacturerType",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.manufacturerType"
    },
    {
      key: "registrationResults.vehicleClass",
      type: FilterTypes.STRING,
      displayName: "objects.report.registrationResults.vehicleClass"
    },
    { key: "message", type: FilterTypes.STRING, displayName: "objects.report.message" },
    { key: "datePreference", type: FilterTypes.STRING, displayName: "objects.report.datePreference" },
    { key: "_id", type: FilterTypes.OBJECT_ID, displayName: "objects.report._id" }
  ];

  hiddenFilter: ThgPageFilterElementGen[] | MrfiktivPageFilterElementGen[] = [
    { key: "progressStatus", value: ProgressStatusEnum.DELETED, operation: "$ne" }
  ];

  @Action
  protected async loadDocuments(query: MrfiktivReportControllerFindAllParamsGen): Promise<any> {
    const res = await reportService.getReportPaginated(query);

    Vue.$log.debug("Paged reports. Parsing for fleet ", res.data?.length);
    FleetAggregationModule.parseReports(res.data ?? []);

    return res;
  }
}

export const ReportPaginationModule = getModule(ReportPaginationStore);
